import type { CellData, SheetData } from '@/components/xlsx-viewer/types';

import type { SkippedRow } from './resultsToUpload';
import { cellText, isErrorCell, type ResultsCell } from './sheetCells';

/** Estimated pixels per character, as the workbook viewer sizes columns. */
const CHAR_PX = 7.2;
const MIN_WIDTH = 60;
/** Fitted columns stop here; "Expand columns" shows the full width. */
const MAX_FITTED_WIDTH = 320;

function toCellData(cell: ResultsCell): CellData {
  if (cell === null || cell === undefined || cellText(cell) === '') {
    return { value: null, type: 'empty' };
  }
  if (isErrorCell(cell)) return { value: cell.error, type: 'string' };
  if (typeof cell === 'number') return { value: cell, type: 'number' };
  if (typeof cell === 'boolean') {
    return { value: cell ? 'TRUE' : 'FALSE', type: 'boolean' };
  }
  return { value: cell, type: 'string' };
}

function widthFor(header: string, cells: CellData[]): number {
  const longest = Math.max(
    header.length,
    ...cells.map(({ value }) => String(value ?? '').length),
  );
  return Math.max(MIN_WIDTH, Math.ceil(longest * CHAR_PX) + 17);
}

/**
 * The rows a layout left out, as a sheet for the spreadsheet viewer: each row
 * as it appears in the supplier's workbook, led by its Excel row number (so it
 * can be found there) and why it was skipped.
 */
export function skippedRowsSheet(
  headers: string[],
  skipped: SkippedRow[],
  labels: {
    rowNumber: string;
    reason: string;
    /** One line saying why a row was skipped. */
    describe: (reason: SkippedRow['reason']) => string;
  },
): SheetData {
  const allHeaders = [labels.rowNumber, labels.reason, ...headers];
  const rows = skipped.map(({ sheetRow, cells, reason }) => [
    { value: sheetRow, type: 'number' as const },
    { value: labels.describe(reason), type: 'string' as const },
    ...headers.map((_, index) => toCellData(cells[index])),
  ]);
  return gridSheet(labels.rowNumber, allHeaders, rows);
}

/** A sheet for the spreadsheet grid, its columns fitted to their content. */
export function gridSheet(
  name: string,
  headers: string[],
  rows: CellData[][],
): SheetData {
  const expandedColumnWidths = headers.map((header, column) =>
    widthFor(
      header,
      rows.map((row) => row[column] ?? { value: null, type: 'empty' }),
    ),
  );
  return {
    name,
    headers,
    rows,
    columnWidths: expandedColumnWidths.map((w) =>
      Math.min(w, MAX_FITTED_WIDTH),
    ),
    expandedColumnWidths,
    totalRows: rows.length,
    truncated: false,
  };
}

/**
 * The upload CSV as a sheet: one row per line, one column per upload column,
 * each cell exactly the text the CSV will carry.
 */
export function uploadPreviewSheet(
  lines: { cells: string[] }[],
  columnLabels: string[],
  name: string,
): SheetData {
  const rows = lines.map(({ cells }) =>
    columnLabels.map((_, index): CellData => {
      const value = cells[index] ?? '';
      return value === ''
        ? { value: null, type: 'empty' }
        : { value, type: 'string' };
    }),
  );
  return gridSheet(name, columnLabels, rows);
}
